import { inject, injectable } from "inversify";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UserStore } from "../../../../services/UserStore";
import { UrlStore } from "../../../../services/urlStore";
import { OrganizationStore } from "../../../../services/OrganizationStore";
import { FiltersStore } from "../../../../services/filtersStore";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { deleteJSON, getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { TFunction } from "i18next";
import { GetPlatformsResponse, ImPlatform, InstallPlatformResponse } from "@whatslly/types/tuvis-messaging/TuvisMessagingPlatforms";
import { PLATFORM_TYPES } from "@whatslly/types/tuvis-messaging/TuvisMessagingPlatforms";
import { UninstallTeamsModal } from "./UninstallTeamsModal";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { GenerateCertificateResponse } from "@whatslly/types/messages-server/destinations";
import { TuvisTeamsAppAuthBodyResponse } from "@whatslly/types/integrations/TuvisTeamsAppAuth";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";

export type TeamsSettingsFormData = {
  connectedAppConsumerKey: string;
  tenantId: string;
  dummyUserId: string;
};

@injectable()
export class TeamsSettingsStore {
  isInitialized = false;
  connected = false;
  isConnecting = false;
  platform?: ImPlatform;
  downloadCertificatePromise: IPromiseBasedObservable<GenerateCertificateResponse | void> | undefined;

  constructor(
    @inject(InjectUserStore) private readonly userStore: UserStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(OrganizationStore) private readonly orgStore: OrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      isInitialized: observable,
      connected: observable,
      isConnecting: observable,
      platform: observable,
      downloadCertificatePromise: observable,
      isReadOnlySettingsUser: computed,
      filters: computed,
      init: action.bound,
      downloadCertificate: action,
      generateServerToServerAccessToken: action,
      certificateData: computed,
    });

    reaction(
      () => this.downloadCertificatePromise?.state,
      () => {
        if (this.downloadCertificatePromise?.state === "fulfilled" && this.downloadCertificatePromise.value?.success === true) {
          downloadBlob(atob(this.downloadCertificatePromise.value.certificate), "tuvis-teams-cert.pem", "application/x-pem-file");
        }
      }
    );
  }

  get isReadOnlySettingsUser() {
    return !hasPermission(this.userStore.user, UserPermissions["teams.update_settings"]);
  }

  get orgId() {
    return this.filtersStore.orgId || this.userStore.user?.organizationId;
  }

  get filters() {
    return { orgId: this.orgId ?? "" };
  }

  async getOrgId() {
    const org = await this.orgStore.selectedOrg;
    return org?.value ?? this.userStore.user?.organizationId;
  }

  async init() {
    try {
      const orgId = await this.getOrgId();
      const authorized = hasPermission(this.userStore.user, UserPermissions["teams.update_settings"] | UserPermissions["teams.view_settings"]);
      if (!orgId || !authorized) {
        this.connected = false;
        this.isInitialized = true;
        return;
      }
      const res = await getJSON({
        urlSuffix: `/integrations/platforms?${new URLSearchParams(this.filters).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken,
      }).then((res) => parseOrReturnError<GetPlatformsResponse>(res, this.logger));

      runInAction(() => {
        if (res.success && Array.isArray(res.platforms)) {
          const teamsPlatform = res.platforms.find((platform: any) => platform.platformType === PLATFORM_TYPES.TUVIS_TEAMS_APP);
          this.connected = !!teamsPlatform && teamsPlatform.active;
          this.platform = this.connected ? teamsPlatform : undefined;
          this.isInitialized = true;
        } else {
          this.connected = false;
          this.isInitialized = true;
        }
      });
    } catch (err: any) {
      this.logger.error({ err }, "Failed to check Teams connection status");
      runInAction(() => {
        this.connected = false;
        this.isInitialized = true;
      });
    }
  }

  get certificateData() {
    if (this.downloadCertificatePromise?.state !== "fulfilled" || !this.downloadCertificatePromise.value?.success) return undefined;
    return {
      privateKey: this.downloadCertificatePromise.value.privateKey,
      certificate: this.downloadCertificatePromise.value.certificate,
    };
  }

  private async getCertificateData(t: TFunction) {
    try {
      const res = await getJSON({
        urlSuffix: `/message/destinations/${await this.orgStore.orgId}/generate-certificate`,
        etoken: this.userStore.etoken ?? "",
      });
      return parseOrReturnError<GenerateCertificateResponse>(res, this.logger);
    } catch (err: any) {
      modalService.openModal(Toast, { text: t("teams_settings_page.download_certificate_failed"), type: "error" });
    }
  }

  async downloadCertificate(t: TFunction) {
    this.downloadCertificatePromise = fromPromise(this.getCertificateData(t));
  }

  async generateServerToServerAccessToken(t: TFunction, formData: TeamsSettingsFormData) {
    if (!this.certificateData) {
      modalService.openModal(Toast, { text: t("teams_settings_page.certificate_required"), type: "error" });
      return;
    }
    const certData = this.certificateData;

    try {
      const res = await postJSON({
        urlSuffix: `/integrations/tuvis-teams-app/server-to-server`,
        body: JSON.stringify({
          privateKey: certData.privateKey,
          certificate: certData.certificate,
          connectedAppConsumerKey: formData.connectedAppConsumerKey,
          tenantId: formData.tenantId,
        }),
      }).then((r) => parseOrReturnError<TuvisTeamsAppAuthBodyResponse>(r, this.logger));
      if (res.success) {
        return res;
      }
      modalService.openModal(Toast, { text: t("teams_settings_page.test_connection_failed"), type: "error" });
    } catch {
      modalService.openModal(Toast, { text: t("teams_settings_page.test_connection_failed"), type: "error" });
    }
  }

  async connectTeams(t: TFunction, formData: TeamsSettingsFormData) {
    runInAction(() => {
      this.isConnecting = true;
    });

    try {
      if (!this.certificateData) {
        throw new Error(t("teams_settings_page.certificate_required"));
      }
      const tokenRes = await this.generateServerToServerAccessToken(t, formData);
      if (!tokenRes?.success) {
        throw new Error(t("teams_settings_page.test_connection_failed"));
      }

      // Install the platform using the encrypted tokens from the server
      const installRes = await postJSON({
        urlSuffix: `/integrations/platforms/install?${new URLSearchParams(this.filters).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify({
          platformType: PLATFORM_TYPES.TUVIS_TEAMS_APP,
          tokens: tokenRes.encryptedTokens,
          integrationData: {
            tenantId: formData.tenantId,
            tenantName: formData.tenantId,
            dummyUserId: formData.dummyUserId,
            appId: formData.connectedAppConsumerKey,
          },
        }),
      }).then((res) => parseOrReturnError<InstallPlatformResponse>(res, this.logger));

      if (!installRes.success) {
        throw new Error(installRes.error || t("teams_settings_page.connection_failed"));
      }

      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: t("teams_settings_page.connected_success"), type: "regular" });
      this.init();
    } catch (err: any) {
      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: err.message || t("teams_settings_page.connection_failed"), type: "error" });
    }
  }

  async disconnect() {
    await deleteJSON({
      urlSuffix: `/integrations/platforms/${this.platform?._id}?${new URLSearchParams(this.filters).toString()}`,
      etoken: this.userStore.etoken ?? "",
      pod: this.userStore.pod,
      body: JSON.stringify({}),
    });

    this.init();
  }

  async openUninstallModal(t: TFunction) {
    const modal = modalService.openModal(UninstallTeamsModal, {
      workspaceName: this.platform?.platformWorkspaceName,
    });
    const result = await modal.promise;

    if (result?.confirmed) {
      try {
        await this.disconnect();
        modalService.openModal(Toast, {
          text: t("teams_settings_page.uninstall_modal.success"),
          type: "regular",
        });
      } catch (err: any) {
        modalService.openModal(Toast, {
          text: t("teams_settings_page.uninstall_modal.error"),
          type: "error",
        });
      }
    }
  }
}
